import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
    ArrowLeft, Loader2, Save, CheckCircle2, AlertCircle, PlusCircle, X,
} from "lucide-react";
import { ImageUpload } from "../../components/ui/image-upload";
import * as Icons from "lucide-react";
import { servicesService, type Service } from "../../../lib/services/servicesService";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

const ICON_NAMES = ["Wind", "Flame", "Snowflake", "Zap", "FlaskConical", "Settings", "Activity", "Box", "Shield"];

const EMPTY_FORM: Partial<Service> = {
    status: "Active",
    color: "bg-primary/10 text-primary border-primary/20",
    colorAccent: "#1a2d50",
    icon: "Settings",
};

export function AdminServiceEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = !id || id === "new";

    const [form, setForm] = useState<Partial<Service>>(EMPTY_FORM);
    const [feats, setFeats] = useState<string[]>([]);
    const [bens, setBens] = useState<{ title: string; desc: string }[]>([]);
    const [faqs, setFaqs] = useState<{ q: string; a: string }[]>([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        servicesService.list()
            .then((services) => {
                const s = services.find((x) => x.$id === id);
                if (s) {
                    setForm(s);
                    try { setFeats(JSON.parse(s.features || "[]")); } catch { setFeats([]); }
                    try { setBens(JSON.parse(s.benefits || "[]")); } catch { setBens([]); }
                    try { setFaqs(JSON.parse(s.faqs || "[]")); } catch { setFaqs([]); }
                }
            })
            .catch(() => showToast("error", "Failed to load service."))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const updateField = (k: keyof Service, v: string) =>
        setForm((f) => ({ ...f, [k]: v }));

    const showToast = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };

    const handleSave = async () => {
        if (!form.title?.trim()) { showToast("error", "Title is required."); return; }
        setSaving(true);
        const payload: Partial<Service> = {
            ...form,
            features: JSON.stringify(feats.filter((f) => f.trim())),
            benefits: JSON.stringify(bens.filter((b) => b.title.trim() || b.desc.trim())),
            faqs: JSON.stringify(faqs.filter((f) => f.q.trim() || f.a.trim())),
        };
        try {
            if (isNew) {
                await servicesService.create(payload as Omit<Service, "$id">);
                showToast("success", "Service created.");
            } else {
                await servicesService.update(id!, payload);
                showToast("success", "Changes saved.");
            }
            setTimeout(() => navigate("/admin/services"), 1200);
        } catch (e: any) {
            showToast("error", e?.message ?? "Failed to save service.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const ServiceIcon = (Icons as any)[form.icon || "Settings"] || Icons.Settings;

    return (
        <div className="w-full min-w-0">
            {/* ── Sticky top bar ── */}
            <div className="sticky top-0 z-10 bg-background border-b border-border">
                <div className="flex items-center gap-3 px-4 sm:px-6 h-14">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/services")}
                        className="gap-1.5 text-muted-foreground hover:text-white -ml-1 shadow-none">
                        <ArrowLeft className="w-4 h-4" /> Services
                    </Button>
                    <div className="h-4 w-px bg-border" />
                    <span className="text-sm font-medium text-foreground truncate flex-1 flex items-center gap-2">
                        {form.icon && <ServiceIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
                        {form.title || (isNew ? "New Service" : "Edit Service")}
                    </span>
                    <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 shadow-none">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {isNew ? "Create Service" : "Save"}
                    </Button>
                </div>
                {toast && (
                    <div className={`flex items-center gap-2 px-6 py-2 text-sm border-t ${toast.type === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-destructive/5 text-destructive border-destructive/10"}`}>
                        {toast.type === "success"
                            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                        {toast.msg}
                    </div>
                )}
            </div>

            {/* ── Two-column body: Content LEFT, Settings RIGHT ── */}
            <div className="flex flex-col lg:flex-row min-h-[calc(100vh-3.5rem)]">

                {/* ── LEFT: Main content ── */}
                <div className="flex-1 p-4 sm:p-6 space-y-6 border-r-0 lg:border-r border-border">

                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Service Title *</Label>
                        <Input placeholder="e.g. HVAC System Installation" value={form.title || ""}
                            onChange={(e) => {
                                updateField("title", e.target.value);
                                if (isNew || !form.slug) {
                                    updateField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, ""));
                                }
                            }}
                            className="shadow-none text-base font-medium h-10" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Short Title <span className="font-normal">(nav/tabs)</span></Label>
                            <Input placeholder="e.g. HVAC" value={form.shortTitle || ""}
                                onChange={(e) => updateField("shortTitle", e.target.value)} className="shadow-none h-9" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">URL Slug</Label>
                            <Input placeholder="e.g. hvac-installation" value={form.slug || ""}
                                onChange={(e) => updateField("slug", e.target.value)} className="shadow-none h-9 font-mono text-sm" />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Description</Label>
                        <Textarea rows={5} placeholder="Describe the service scope and value proposition…"
                            value={form.desc || ""} onChange={(e) => updateField("desc", e.target.value)}
                            className="resize-y shadow-none min-h-[120px]" />
                    </div>

                    <div className="border-t border-border" />

                    {/* Features */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Features ({feats.length})</p>
                            <Button variant="outline" size="sm" onClick={() => setFeats([...feats, ""])}
                                className="h-7 text-xs shadow-none gap-1">
                                <PlusCircle className="w-3.5 h-3.5" /> Add
                            </Button>
                        </div>
                        {feats.length === 0
                            ? <p className="text-xs text-muted-foreground italic">No features yet — click Add to start.</p>
                            : (
                                <div className="space-y-2">
                                    {feats.map((feat, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                            <Input value={feat}
                                                onChange={(e) => { const c = [...feats]; c[i] = e.target.value; setFeats(c); }}
                                                placeholder={`Feature ${i + 1}`} className="h-8 text-sm shadow-none flex-1" />
                                            <Button variant="ghost" size="sm"
                                                onClick={() => setFeats(feats.filter((_, idx) => idx !== i))}
                                                className="h-8 px-2 text-destructive hover:bg-destructive/10 shrink-0">
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                    </div>

                    {/* Benefits */}
                    <div className="space-y-3 border-t border-border pt-4">
                        <div className="flex items-center justify-between">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Benefits ({bens.length})</p>
                            <Button variant="outline" size="sm" onClick={() => setBens([...bens, { title: "", desc: "" }])}
                                className="h-7 text-xs shadow-none gap-1">
                                <PlusCircle className="w-3.5 h-3.5" /> Add
                            </Button>
                        </div>
                        {bens.length === 0
                            ? <p className="text-xs text-muted-foreground italic">No benefits yet.</p>
                            : (
                                <div className="space-y-3">
                                    {bens.map((ben, i) => (
                                        <div key={i} className="flex gap-2 border border-border rounded-md p-3">
                                            <div className="flex-1 space-y-2">
                                                <Input value={ben.title}
                                                    onChange={(e) => { const c = [...bens]; c[i].title = e.target.value; setBens(c); }}
                                                    placeholder="Benefit title" className="h-8 text-sm font-semibold shadow-none" />
                                                <Textarea value={ben.desc}
                                                    onChange={(e) => { const c = [...bens]; c[i].desc = e.target.value; setBens(c); }}
                                                    placeholder="Description…" className="text-sm min-h-[56px] resize-none shadow-none" />
                                            </div>
                                            <Button variant="ghost" size="sm"
                                                onClick={() => setBens(bens.filter((_, idx) => idx !== i))}
                                                className="h-8 px-2 shrink-0 text-destructive hover:bg-destructive/10">
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                    </div>

                    {/* FAQs */}
                    <div className="space-y-3 border-t border-border pt-4">
                        <div className="flex items-center justify-between">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">FAQs ({faqs.length})</p>
                            <Button variant="outline" size="sm" onClick={() => setFaqs([...faqs, { q: "", a: "" }])}
                                className="h-7 text-xs shadow-none gap-1">
                                <PlusCircle className="w-3.5 h-3.5" /> Add
                            </Button>
                        </div>
                        {faqs.length === 0
                            ? <p className="text-xs text-muted-foreground italic">No FAQs yet.</p>
                            : (
                                <div className="space-y-3">
                                    {faqs.map((faq, i) => (
                                        <div key={i} className="flex gap-2 border border-border rounded-md p-3">
                                            <div className="flex-1 space-y-2">
                                                <Input value={faq.q}
                                                    onChange={(e) => { const c = [...faqs]; c[i].q = e.target.value; setFaqs(c); }}
                                                    placeholder="Question?" className="h-8 text-sm font-medium shadow-none" />
                                                <Textarea value={faq.a}
                                                    onChange={(e) => { const c = [...faqs]; c[i].a = e.target.value; setFaqs(c); }}
                                                    placeholder="Answer…" className="text-sm min-h-[56px] resize-none shadow-none" />
                                            </div>
                                            <Button variant="ghost" size="sm"
                                                onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))}
                                                className="h-8 px-2 shrink-0 text-destructive hover:bg-destructive/10">
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )
                        }
                    </div>
                </div>

                {/* ── RIGHT: Settings sidebar ── */}
                <div className="w-full lg:w-72 xl:w-80 shrink-0 p-4 sm:p-6 space-y-5 border-t lg:border-t-0 border-border bg-muted/20">

                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Service Settings</p>

                    {/* Status + Order */}
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                            <Select value={form.status} onValueChange={(v) => updateField("status", v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Active">Active</SelectItem>
                                    <SelectItem value="Inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Sort Order</Label>
                            <Input type="number" placeholder="1" value={form.sortOrder || ""}
                                onChange={(e) => updateField("sortOrder", e.target.value)} className="shadow-none h-9" />
                        </div>
                    </div>

                    {/* Icon + Accent */}
                    <div className="space-y-3 border-t border-border pt-4">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Icon</Label>
                            <Select value={form.icon || "Settings"} onValueChange={(v) => updateField("icon", v)}>
                                <SelectTrigger className="shadow-none h-9">
                                    <div className="flex items-center gap-2">
                                        {(() => { const Ic = (Icons as any)[form.icon || "Settings"] || Icons.Settings; return <Ic className="w-4 h-4" />; })()}
                                        <SelectValue />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    {ICON_NAMES.map((n) => {
                                        const Ic = (Icons as any)[n];
                                        return (
                                            <SelectItem key={n} value={n}>
                                                <div className="flex items-center gap-2"><Ic className="w-4 h-4" />{n}</div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Accent Color</Label>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-9 rounded border border-border shrink-0 overflow-hidden">
                                    <input type="color" value={form.colorAccent || "#1a2d50"}
                                        onChange={(e) => updateField("colorAccent", e.target.value)}
                                        className="w-10 h-10 -mt-0.5 -ml-0.5 cursor-pointer border-0 bg-transparent" />
                                </div>
                                <Input placeholder="#1a2d50" value={form.colorAccent || ""}
                                    onChange={(e) => updateField("colorAccent", e.target.value)}
                                    className="shadow-none h-9 font-mono text-xs flex-1" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Badge Classes</Label>
                            <Input placeholder="bg-primary/10 text-primary border-primary/20"
                                value={form.color || ""}
                                onChange={(e) => updateField("color", e.target.value)}
                                className="shadow-none h-9 font-mono text-xs" />
                        </div>
                    </div>

                    {/* Hero image */}
                    <div className="space-y-2 border-t border-border pt-4">
                        <ImageUpload
                            label="Hero Image"
                            value={form.img || ""}
                            onChange={(url) => updateField("img", url)}
                            aspectRatio="video"
                        />
                    </div>

                    {/* Preview badges */}
                    <div className="pt-2 border-t border-border flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${form.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground"}`}>
                            {form.status || "Draft"}
                        </Badge>
                        {form.color && (
                            <Badge variant="outline" className={`text-xs ${form.color}`}>
                                {form.shortTitle || form.title || "Badge"}
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
